import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

class Untreated {
	constructor (container) {
		// All dom elements
		this.el = {
			container: container,
			wrapper: document.querySelector('.untreated-wrapper'),
			nav: document.querySelector('#navbar_wrapper'),
			svg: document.querySelector('.untreated-svg'),
			textBrain: document.querySelector('.untreated-animation-brain'),
			textHeart: document.querySelector('.untreated-animation-heart'),
			textIntestines: document.querySelector('.untreated-animation-intestines'),
			textListBrain: document.querySelector('.untreated-text-list-brain'),
			textLists: document.querySelectorAll('.untreated-text-list'),
			textToggles: document.querySelectorAll('.untreated-text-toggle'),
			toggles: document.querySelectorAll('.untreated-toggle'),
			highlightBackground: document.querySelector('.untreated-highlight-background'),
			highlight: document.querySelector('.untreated-highlight')
		};
		this.tl = null;
		this.containerHeight = window.innerHeight;

		let prevHeight = window.innerHeight;
		let prevWidth = window.innerWidth;
		window.addEventListener('resize', () => {
			// This is to avoid jumps when scrolling on iOs and making the UI slide down triggering a resize event
			if (
				// If the width changed -> Update
				(prevWidth !== window.innerWidth) ||
				// If the height of the window is bigger than before, and bigger than container -> Update
				(prevHeight < window.innerHeight && window.innerHeight > this.containerHeight) 
				) {
				this.setContainerHeight();
				this.toggleContentVisibility();
			}
			this.setPopupOffset();
			// Update the previous size dimensions
			prevHeight = window.innerHeight;
			prevWidth = window.innerWidth;
		});

		this.setContainerHeight();
		this.setPopupOffset();
		this.setupMobilePanel();
		this.toggleContentVisibility();
		this.setupTimeline();
	}

	setContainerHeight () {
		let topOffset = this.el.nav.offsetHeight;
		this.el.wrapper.style.height = window.innerHeight - topOffset + 'px';
		this.containerHeight = window.innerHeight;
	}

	/* Define the bottom position of tooltips */
	setPopupOffset () {
		// This offset is needed when the animation height is bigger than the window height on mobile
		this.el.wrapper.style.setProperty('--bottom-offset', (this.containerHeight - window.innerHeight) + 'px');
	}

	setupTimeline () {
		/* Main timeline */
		this.tl = gsap.timeline({
			scrollTrigger: {
				trigger: this.el.container.parentElement,
				pin: true,
				scrub: 0.2,
				start: () => {
					let topOffset = this.el.nav.offsetHeight;
					topOffset += parseFloat(window.getComputedStyle(this.el.wrapper).marginTop);
					return `top ${topOffset}px`;
				},
				end: () => {
					let totalHeight = 0;
					if (this.isMobile()) {
						// On mobile, 6 * (max height available);
						totalHeight = this.containerHeight * 6;
					} else {
						// On desktop, always 8 * window height
						totalHeight = window.innerHeight * 8;
					}
					return `+=${totalHeight}`;
				}
			}
		});

		/* Little hack to define a fixed total duration on the timeline  */
		this.tl.to({}, {
			duration: 1
		});

		/* Zoom on the head */
		this.tl.to(this.el.svg, {
			scale: 2,
			yPercent: 15,
			duration: 0.15,
			ease: 'power2.inOut'
		}, 0);

		/* Put the highlight on the brain */
		this.tl.add(() => {
			const tl = gsap.timeline().fromTo([this.el.highlight, this.el.highlightBackground], {
				cx: 960,
				cy: 210
			}, {
				cx: 960,
				cy: 210,
				duration: this.tl.scrollTrigger.direction === 1 ? 1 : 0.6,
				ease: 'power1.inOut',
				yoyoEase: true
			});
			if (this.tl.scrollTrigger.direction === -1) {
				tl.reverse(0);
			}
		}, 0.08);

		/* Move to the belly */
		this.tl.to(this.el.svg, {
			yPercent: () => {
				if (this.isMobile() && this.isLandscape()) {
					return -80;
				} else {
					return -60;
				}
			},
			duration: 0.15,
			ease: 'power2.inOut'
		}, 0.33);

		/* Put the highlight on the heart */
		this.tl.add(() => {
			const tl = gsap.timeline().fromTo([this.el.highlight, this.el.highlightBackground], {
				cx: 960,
				cy: 210
			}, {
				cx: 1020,
				cy: 1250,
				duration: this.tl.scrollTrigger.direction === 1 ? 1 : 0.6,
				ease: 'power1.inOut',
				yoyoEase: true
			});
			if (this.tl.scrollTrigger.direction === -1) {
				tl.reverse(0);
			}
		}, 0.4);

		/* Move to the intestines */
		this.tl.to(this.el.svg, {
			yPercent: () => {
				if (this.isMobile() && this.isLandscape()) {
					return -170;
				} else {
					return -140;
				}
			},
			duration: 0.15,
			ease: 'power2.inOut'
		}, 0.66);


		/* Put the highlight on the intestines */
		this.tl.add(() => {
			const tl = gsap.timeline().fromTo([this.el.highlight, this.el.highlightBackground], {
				cx: 1020,
				cy: 1250
			}, {
				cx: 1010,
				cy: 2000,
				duration: this.tl.scrollTrigger.direction === 1 ? 1 : 0.6,
				ease: 'power1.inOut',
				yoyoEase: true
			});
			if (this.tl.scrollTrigger.direction === -1) {
				tl.reverse(0);
			}
		}, 0.68);

		/* ============ */
		/* TEXTS TWEENS */
		/* ============ */
		gsap.set([this.el.textBrain, this.el.textHeart, this.el.textIntestines], {
			autoAlpha: 0
		});

		/* Fade in the brain text */
		this.tl.add(() => {
			return this.switchToBrainText(this.tl.scrollTrigger.direction === -1);
		}, 0.08);
		/* Fade in the heart text */
		this.tl.add(() => {
			return this.switchToHeartText(this.tl.scrollTrigger.direction === -1);
		}, 0.4);
		/* Fade in the intestines text */
		this.tl.add(() => {
			return this.switchToIntestinesText(this.tl.scrollTrigger.direction === -1);
		}, 0.68);
	}

	/* Scrolling to the brain */
	switchToBrainText (reverse) {
		const tl = gsap.timeline()
		.fromTo(this.el.textBrain, {
			y: 100,
			autoAlpha: 0
		}, {
			autoAlpha: 1,
			y: 0,
			duration: 0.4,
			ease: 'power2.out'
		});
		if (reverse) {
			tl.reverse(0);
		}
		return tl;
	}

	/* Scrolling to the heart */
	switchToHeartText (reverse) {
		const tl = gsap.timeline()
			.fromTo(this.el.textBrain, {
				autoAlpha: 1,
				y: 0
			}, {
				autoAlpha: 0,
				y: 50,
				duration: 0.2,
				ease: 'power1.in'
			})
			.call(() => gsap.set(this.el.textBrain, {
				display: reverse ? 'block' : 'none'
			}))
			.call(() => gsap.set(this.el.textHeart, {
				display: reverse ? 'none' : 'block'
			}))
			.fromTo(this.el.textHeart, {
				y: 100,
				autoAlpha: 0
			}, {
				autoAlpha: 1,
				y: 0,
				duration: 0.4,
				ease: 'power2.out'
			});

		if (reverse) {
			tl.reverse(0);
		}

		return tl;
	}

	/* Scrolling to the intestines */
	switchToIntestinesText (reverse) {
		const tl = gsap.timeline()
			.fromTo(this.el.textHeart, {
				autoAlpha: 1,
				y: 0
			}, {
				autoAlpha: 0,
				y: 50,
				duration: 0.2,
				ease: 'power1.in'
			})
			.call(() => gsap.set(this.el.textHeart, {
				display: reverse ? 'block' : 'none'
			}))
			.call(() => gsap.set(this.el.textIntestines, {
				display: reverse ? 'none' : 'block'
			}))
			.fromTo(this.el.textIntestines, {
				y: 100,
				autoAlpha: 0
			}, {
				autoAlpha: 1,
				y: 0,
				duration: 0.4,
				ease: 'power2.out'
			});

		if (reverse) {
			tl.reverse(0);
		}

		return tl;
	}

	/* Detect if the current screen matches the mobile media query */
	isMobile () {
		return window.matchMedia('(min-width: 0em) and (max-width: 57.5525em)').matches;
	}
	/* Detect if the current screen matches is on landscape */
	isLandscape () {
		return window.matchMedia('(orientation:landscape)').matches;
	}

	/* Mobile interaction with the box */
	setupMobilePanel () {
		/* When clicking on the arrow or the 'Read more' buttons */
		this.el.textToggles.forEach(el => {
			el.addEventListener('click', () => this.onToggleClick(el));
		});
		/* When clicking on the arrow toggle */
		this.el.toggles.forEach(el => {
			el.addEventListener('click', () => this.onToggleClick(el));
		});
	}

	onToggleClick (elToggle) {
		let closestCopy = elToggle.parentElement.querySelector('.untreated-text-list');
		/* If copy was visible, close it */
		if (this.isCopyVisible) {
			gsap.to(closestCopy, {
				height: 0,
				duration: 0.5,
				ease: 'power1.out'
			});
			this.isCopyVisible = false;
			// Update the copy of the button
			elToggle.innerHTML = elToggle.dataset.more;
		} else {
			/* Expand the copy */
			gsap.to(closestCopy, {
				height: 'auto',
				duration: 0.5,
				ease: 'power1.out'
			});
			this.isCopyVisible = true;
			// Update the copy of the button
			elToggle.innerHTML = elToggle.dataset.less;
		}

		// Add a class on the parent to make the buttons arrows rotate
		elToggle.parentElement.classList.toggle('is-copy-expanded', this.isCopyVisible);
	}

	toggleContentVisibility () {
		if (this.isMobile()) {
			// On page load, hide the copy
			gsap.set(this.el.textLists, {
				height: 0,
				overflow: 'hidden'
			});
		} else if (!this.isMobile()) {
			// On desktop, always show the copy
			gsap.set(this.el.textLists, {
				height: 'auto',
				overflow: 'hidden'
			});
		}
	}
}

export default Untreated;
